import { Injectable } from '@angular/core';

type PaletteName = 'primary' | 'alt1' | 'alt2' | 'alt3';
type ChartPaletteGroup = 'bar' | 'line' | 'pie';

const PALETTE_SEQUENCE: PaletteName[] = ['primary', 'alt1', 'alt2', 'alt3'];

const BAR_CHART_PALETTES: Record<PaletteName, string[]> = {
  primary: [
    '#0F2C2F', // $primary-darker
    '#2ed9c2', // $primary
    '#a3dfd7', // $primary-light
    '#DCF2E8', // $light-alt
  ],
  alt1: [
    '#0F2C2F', // $primary-darker
    '#3d46f2', // $alt-1
    '#8d91de', /// $alt-1-light
    '#DCF2E8', // $light-alt
  ],
  alt2: [
    '#0F2C2F', // $primary-darker
    '#D85AE2', // $alt-2
    '#e0a3e4', // $alt-2-light
    '#DCF2E8', // $light-alt
  ],
  alt3: [
    '#0F2C2F', // $primary-darker
    '#fa8671', // $alt-3
    '#f0aea3', // $alt-3-light
    '#DCF2E8', // $light-alt
  ],
};

const PIE_CHART_PALETTES: Record<PaletteName, string[]> = {
  primary: [
    '#0F2C2F', // $primary-darker
    '#2ed9c2', // $primary
    '#DCF2E8', // $light-alt
    '#3d46f2', // $alt-1
    '#D85AE2', // $alt-2
  ],
  alt1: [
    '#0F2C2F', // $primary-darker
    '#3d46f2', // $alt-1
    '#DCF2E8', // $light-alt
    '#2ed9c2', // $primary
    '#D85AE2', // $alt-2
  ],
  alt2: [
    '#0F2C2F', // $primary-darker
    '#D85AE2', // $alt-2
    '#DCF2E8', // $light-alt
    '#2ed9c2', // $primary
    '#3d46f2', // $alt-1
  ],
  alt3: [
    '#3d46f2', // $alt-1
    '#fa8671', // $alt-3
    '#DCF2E8', // $light-alt
    '#0F2C2F', // $primary-darker
    '#D85AE2', // $alt-2
  ],
};

const CHART_PALETTES: Record<
  ChartPaletteGroup,
  Record<PaletteName, string[]>
> = {
  bar: BAR_CHART_PALETTES,
  line: BAR_CHART_PALETTES,
  pie: PIE_CHART_PALETTES,
};

@Injectable({
  providedIn: 'root',
})
export class ChartThemeService {
  getSeriesColors(
    index: number,
    options?: {
      palette?: PaletteName;
      seriesCount?: number;
      chartType?: ChartPaletteGroup;
    }
  ): string[] {
    const paletteName =
      options?.palette ?? this._resolvePaletteName(this._normalizeIndex(index));
    const chartType = options?.chartType ?? 'bar';
    const paletteGroup = CHART_PALETTES[chartType] ?? CHART_PALETTES.bar;
    const basePalette = paletteGroup[paletteName] ?? paletteGroup.primary ?? [];

    if (!options?.seriesCount || options.seriesCount <= basePalette.length) {
      return basePalette.slice(0, options?.seriesCount ?? basePalette.length);
    }

    const colors: string[] = [];
    while (colors.length < options.seriesCount) {
      colors.push(...basePalette);
    }

    return colors.slice(0, options.seriesCount);
  }

  getSliceColors(subcategoryIndex: number, sliceCount: number): string[] {
    const palette = this.getSeriesColors(
      this._normalizeIndex(subcategoryIndex),
      {
        seriesCount: sliceCount,
        chartType: 'pie',
      }
    );
    return palette.slice(0, sliceCount);
  }

  private _resolvePaletteName(index: number): PaletteName {
    const normalized = this._normalizeIndex(index);
    return PALETTE_SEQUENCE[normalized];
  }

  private _normalizeIndex(index: number): number {
    const paletteLength = PALETTE_SEQUENCE.length;
    if (!Number.isFinite(index) || paletteLength === 0) {
      return 0;
    }
    const normalized = index % paletteLength;
    return normalized < 0 ? normalized + paletteLength : normalized;
  }
}
